/**
 * What an audio file's own headers say about the audio in it: the container,
 * and the bit rate.
 *
 * The tags say who made a track. They say nothing about the encode, and
 * neither does any drive API for two of the three drives (OneDrive has an
 * `audio` facet, Drive and Dropbox have a name and a size). So this reads the
 * bytes that carry it - a few KB at the front of the file, over the same
 * range requests the tag read makes - and nothing more:
 *
 * - MP3: the first MPEG frame header after the ID3v2 tag. A Xing or VBRI
 *   header in that frame gives the frame and byte counts of a variable bit
 *   rate encode, and the average is worked out from those; without one the
 *   frame's own bit rate is the file's.
 * - MP4 / M4A: `mvhd` for the duration, `mdat` for the audio bytes. The
 *   average of the two is the bit rate; the atoms are only walked, never
 *   read, so artwork in `moov` costs nothing.
 * - FLAC: STREAMINFO for the sample count, the file size less the metadata
 *   blocks for the bytes.
 * - WAV: the `fmt ` chunk's byte rate.
 * - AIFF / AIFF-C: the `COMM` chunk, for PCM encodes.
 * - Ogg Vorbis: the nominal bit rate from the identification header.
 *
 * Anything else - Opus, WMA, APE, a file the sync scan cannot find a frame in
 * - comes back without a bit rate rather than with a guess. An estimate from
 * size over duration would count the artwork, so it is not made.
 *
 * Pure over a ByteSource so it runs against a Uint8Array in tests and against
 * jsmediatags' XhrFileReader in the app (services/jsmediatags/audioPropertiesReader).
 */

export interface ByteSource {
  /** The file size in bytes. */
  readonly size: number;
  /**
   * Makes bytes `start` to `end`, inclusive, available to byteAt. `end` past
   * the size is clamped; a source is free to fetch more than asked.
   */
  load(start: number, end: number): Promise<void>;
  /** A byte that load has made available. */
  byteAt(offset: number): number;
}

export type AudioContainer = 'mp3' | 'm4a' | 'flac' | 'wav' | 'aiff' | 'ogg';

export interface AudioProperties {
  /** What the bytes are, whatever the file is called. */
  container: AudioContainer;
  /** Average kilobits per second, whole. Absent when the header does not say. */
  bitrate?: number;
  /**
   * MP3 only: the encode carries a Xing or VBRI header, so the bit rate is
   * an average over a stream that varies. Absent when it is constant, and
   * for the containers that do not say.
   */
  vbr?: true;
}

/** A Uint8Array as a ByteSource, for tests and for bytes already in hand. */
export function bytesSource(bytes: Uint8Array): ByteSource {
  return {
    size: bytes.length,
    load: () => Promise.resolve(),
    byteAt: (offset) => bytes[offset],
  };
}

/** How far past the ID3 tag the MP3 sync scan looks before giving up. */
const MP3_SCAN_LIMIT = 64 * 1024;

function ascii(source: ByteSource, offset: number, length: number) {
  let out = '';

  for (let i = 0; i < length; i++) {
    out += String.fromCharCode(source.byteAt(offset + i));
  }

  return out;
}

function u16be(source: ByteSource, offset: number) {
  return (source.byteAt(offset) << 8) | source.byteAt(offset + 1);
}

function u24be(source: ByteSource, offset: number) {
  return (
    (source.byteAt(offset) << 16) | (source.byteAt(offset + 1) << 8) | source.byteAt(offset + 2)
  );
}

/** `>>> 0` keeps the top bit from making it negative. */
function u32be(source: ByteSource, offset: number) {
  return (
    ((source.byteAt(offset) << 24) |
      (source.byteAt(offset + 1) << 16) |
      (source.byteAt(offset + 2) << 8) |
      source.byteAt(offset + 3)) >>>
    0
  );
}

function u32le(source: ByteSource, offset: number) {
  return (
    (source.byteAt(offset) |
      (source.byteAt(offset + 1) << 8) |
      (source.byteAt(offset + 2) << 16) |
      (source.byteAt(offset + 3) << 24)) >>>
    0
  );
}

/** Two 32-bit halves as one number; exact below 2^53, which any file is. */
function u64be(source: ByteSource, offset: number) {
  return u32be(source, offset) * 0x100000000 + u32be(source, offset + 4);
}

/** kbps from bits and seconds, or nothing when either is missing. */
function kbps(bytes: number, seconds: number) {
  if (!(bytes > 0) || !(seconds > 0)) return undefined;

  return Math.round((bytes * 8) / seconds / 1000);
}

/**
 * Where the audio starts: after every ID3v2 tag at the front (there can be
 * more than one, though one is the rule), including a v2.4 footer.
 */
async function skipId3v2(source: ByteSource) {
  let offset = 0;

  for (;;) {
    if (offset + 10 > source.size) return offset;

    await source.load(offset, offset + 9);

    if (ascii(source, offset, 3) !== 'ID3') return offset;

    const flags = source.byteAt(offset + 5);
    const size =
      (source.byteAt(offset + 6) << 21) |
      (source.byteAt(offset + 7) << 14) |
      (source.byteAt(offset + 8) << 7) |
      source.byteAt(offset + 9);

    offset += 10 + size + (flags & 0x10 ? 10 : 0);
  }
}

/*
 * MPEG audio frame headers. Versions index the tables as 0 = MPEG 2.5,
 * 2 = MPEG 2, 3 = MPEG 1 - the header's own two bits - and 1 is reserved.
 */

const BITRATES_V1: { [layer: number]: number[] } = {
  1: [0, 32, 64, 96, 128, 160, 192, 224, 256, 288, 320, 352, 384, 416, 448],
  2: [0, 32, 48, 56, 64, 80, 96, 112, 128, 160, 192, 224, 256, 320, 384],
  3: [0, 32, 40, 48, 56, 64, 80, 96, 112, 128, 160, 192, 224, 256, 320],
};

/** MPEG 2 and 2.5 share these; layers II and III share one table. */
const BITRATES_V2: { [layer: number]: number[] } = {
  1: [0, 32, 48, 56, 64, 80, 96, 112, 128, 144, 160, 176, 192, 224, 256],
  2: [0, 8, 16, 24, 32, 40, 48, 56, 64, 80, 96, 112, 128, 144, 160],
  3: [0, 8, 16, 24, 32, 40, 48, 56, 64, 80, 96, 112, 128, 144, 160],
};

const SAMPLE_RATES: { [version: number]: number[] } = {
  3: [44100, 48000, 32000],
  2: [22050, 24000, 16000],
  0: [11025, 12000, 8000],
};

interface MpegFrame {
  version: number;
  layer: number;
  bitrate: number;
  sampleRate: number;
  /** Bytes, this frame's header included. */
  length: number;
  samples: number;
  mono: boolean;
}

/** The four header bytes at `offset` as a frame, or nothing if they are not one. */
function mpegFrameAt(source: ByteSource, offset: number): MpegFrame | undefined {
  const b1 = source.byteAt(offset);
  const b2 = source.byteAt(offset + 1);
  const b3 = source.byteAt(offset + 2);
  const b4 = source.byteAt(offset + 3);

  if (b1 !== 0xff || (b2 & 0xe0) !== 0xe0) return undefined;

  const version = (b2 >> 3) & 3;
  // The layer bits count down: 3 is Layer I, 1 is Layer III.
  const layer = 4 - ((b2 >> 1) & 3);
  const bitrateIndex = (b3 >> 4) & 15;
  const sampleRateIndex = (b3 >> 2) & 3;
  const padding = (b3 >> 1) & 1;
  const channelMode = (b4 >> 6) & 3;

  if (version === 1 || layer === 4 || bitrateIndex === 0 || bitrateIndex === 15) return undefined;
  if (sampleRateIndex === 3) return undefined;

  const bitrate = (version === 3 ? BITRATES_V1 : BITRATES_V2)[layer][bitrateIndex];
  const sampleRate = SAMPLE_RATES[version][sampleRateIndex];

  let samples: number;
  let length: number;

  if (layer === 1) {
    samples = 384;
    length = (Math.floor((12 * bitrate * 1000) / sampleRate) + padding) * 4;
  } else if (layer === 2 || version === 3) {
    samples = 1152;
    length = Math.floor((144 * bitrate * 1000) / sampleRate) + padding;
  } else {
    // Layer III at a lower version: half the samples per frame.
    samples = 576;
    length = Math.floor((72 * bitrate * 1000) / sampleRate) + padding;
  }

  return { version, layer, bitrate, sampleRate, length, samples, mono: channelMode === 3 };
}

/**
 * Finds the first real frame from `start`: a header whose successor, at the
 * length it claims, is a header of the same kind. Padding and stray 0xFF
 * bytes after a tag are what the second check is for.
 */
async function findMpegFrame(source: ByteSource, start: number) {
  const end = Math.min(source.size, start + MP3_SCAN_LIMIT);

  await source.load(start, end - 1);

  for (let offset = start; offset + 4 <= end; offset++) {
    const frame = mpegFrameAt(source, offset);

    if (!frame) continue;

    const next = offset + frame.length;

    // A file with a single frame. Trust what there is.
    if (next + 4 > source.size) return { offset, frame };

    await source.load(next, next + 3);

    const nextFrame = mpegFrameAt(source, next);

    if (
      nextFrame &&
      nextFrame.version === frame.version &&
      nextFrame.layer === frame.layer &&
      nextFrame.sampleRate === frame.sampleRate
    ) {
      return { offset, frame };
    }
  }

  return undefined;
}

async function readMp3(source: ByteSource, start: number): Promise<AudioProperties | undefined> {
  const found = await findMpegFrame(source, start);

  if (!found) return undefined;

  const { offset, frame } = found;

  await source.load(offset, offset + frame.length - 1);

  // The Xing / Info header sits after the side information, whose size
  // depends on the version and the channel count.
  const sideInfo = frame.version === 3 ? (frame.mono ? 17 : 32) : frame.mono ? 9 : 17;
  const xing = offset + 4 + sideInfo;

  if (xing + 16 <= offset + frame.length && xing + 16 <= source.size) {
    const tag = ascii(source, xing, 4);

    if (tag === 'Xing' || tag === 'Info') {
      const flags = u32be(source, xing + 4);
      let at = xing + 8;
      let frames = 0;
      let bytes = 0;

      if (flags & 1) {
        frames = u32be(source, at);
        at += 4;
      }
      if (flags & 2) {
        bytes = u32be(source, at);
      }

      // LAME writes "Info" for a constant bit rate and "Xing" for a variable
      // one. Only the latter needs the average worked out.
      if (tag === 'Xing') {
        const bitrate = kbps(bytes, (frames * frame.samples) / frame.sampleRate);

        return bitrate ? { container: 'mp3', bitrate, vbr: true } : { container: 'mp3' };
      }

      return { container: 'mp3', bitrate: frame.bitrate };
    }
  }

  // Fraunhofer's VBR header is at a fixed offset instead.
  const vbri = offset + 4 + 32;

  if (
    vbri + 18 <= offset + frame.length &&
    vbri + 18 <= source.size &&
    ascii(source, vbri, 4) === 'VBRI'
  ) {
    const bytes = u32be(source, vbri + 10);
    const frames = u32be(source, vbri + 14);
    const bitrate = kbps(bytes, (frames * frame.samples) / frame.sampleRate);

    return bitrate ? { container: 'mp3', bitrate, vbr: true } : { container: 'mp3' };
  }

  return { container: 'mp3', bitrate: frame.bitrate };
}

/** An atom's size and where its payload begins, given the 8 or 16 byte header at `offset`. */
async function mp4Atom(source: ByteSource, offset: number) {
  await source.load(offset, offset + 15);

  const size32 = u32be(source, offset);
  const type = ascii(source, offset + 4, 4);

  if (size32 === 1) {
    return { type, size: u64be(source, offset + 8), payload: offset + 16 };
  }

  // Zero means "to the end of the file".
  return { type, size: size32 === 0 ? source.size - offset : size32, payload: offset + 8 };
}

async function readMp4(source: ByteSource, start: number): Promise<AudioProperties | undefined> {
  let mdatBytes = 0;
  let seconds: number | undefined;
  let offset = start;

  while (offset + 8 <= source.size) {
    const atom = await mp4Atom(source, offset);

    if (atom.size < 8) break;

    if (atom.type === 'mdat') {
      mdatBytes += atom.size - (atom.payload - offset);
    } else if (atom.type === 'moov') {
      const end = offset + atom.size;
      let child = atom.payload;

      while (child + 8 <= end) {
        const inner = await mp4Atom(source, child);

        if (inner.size < 8) break;

        if (inner.type === 'mvhd') {
          await source.load(inner.payload, inner.payload + 31);

          const version = source.byteAt(inner.payload);

          // Version 1 has 64-bit creation and modification times ahead of
          // the timescale, version 0 32-bit ones.
          const timescale =
            version === 1 ? u32be(source, inner.payload + 20) : u32be(source, inner.payload + 12);
          const duration =
            version === 1 ? u64be(source, inner.payload + 24) : u32be(source, inner.payload + 16);

          if (timescale > 0 && duration > 0) seconds = duration / timescale;
          break;
        }

        child += inner.size;
      }
    }

    offset += atom.size;
  }

  const bitrate = seconds === undefined ? undefined : kbps(mdatBytes, seconds);

  return bitrate ? { container: 'm4a', bitrate } : { container: 'm4a' };
}

async function readFlac(source: ByteSource, start: number): Promise<AudioProperties | undefined> {
  let offset = start + 4;
  let sampleRate = 0;
  let totalSamples = 0;

  for (;;) {
    if (offset + 4 > source.size) break;

    await source.load(offset, offset + 3);

    const header = source.byteAt(offset);
    const last = (header & 0x80) !== 0;
    const type = header & 0x7f;
    const length = u24be(source, offset + 1);

    if (type === 0 && length >= 18) {
      const body = offset + 4;

      await source.load(body, body + 17);

      sampleRate =
        (source.byteAt(body + 10) << 12) |
        (source.byteAt(body + 11) << 4) |
        (source.byteAt(body + 12) >> 4);
      // 36 bits: the low nibble of byte 13 and the four after it.
      totalSamples = (source.byteAt(body + 13) & 0x0f) * 0x100000000 + u32be(source, body + 14);
    }

    offset += 4 + length;

    if (last) break;
  }

  // Everything after the last metadata block is frames.
  const bitrate =
    sampleRate > 0 ? kbps(source.size - offset, totalSamples / sampleRate) : undefined;

  return bitrate ? { container: 'flac', bitrate } : { container: 'flac' };
}

async function readWav(source: ByteSource, start: number): Promise<AudioProperties | undefined> {
  let offset = start + 12;

  while (offset + 8 <= source.size) {
    await source.load(offset, offset + 7);

    const id = ascii(source, offset, 4);
    const length = u32le(source, offset + 4);

    if (id === 'fmt ' && length >= 16) {
      await source.load(offset + 8, offset + 8 + 15);

      const byteRate = u32le(source, offset + 16);
      const bitrate = byteRate > 0 ? Math.round((byteRate * 8) / 1000) : undefined;

      return bitrate ? { container: 'wav', bitrate } : { container: 'wav' };
    }

    // Chunks are word aligned.
    offset += 8 + length + (length & 1);
  }

  return { container: 'wav' };
}

/** An 80-bit IEEE 754 extended float, which is how AIFF writes the sample rate. */
function extended80(source: ByteSource, offset: number) {
  const exponent = u16be(source, offset) & 0x7fff;
  const mantissa = u64be(source, offset + 2);

  if (exponent === 0 && mantissa === 0) return 0;

  return mantissa * Math.pow(2, exponent - 16383 - 63);
}

/** The AIFF-C compression types that are still PCM, so the COMM arithmetic holds. */
const PCM_AIFC = new Set(['NONE', 'sowt', 'twos', 'in24', 'in32', 'fl32', 'fl64', 'FL32', 'FL64']);

async function readAiff(source: ByteSource, start: number): Promise<AudioProperties | undefined> {
  const compressed = ascii(source, start + 8, 4) === 'AIFC';
  let offset = start + 12;

  while (offset + 8 <= source.size) {
    await source.load(offset, offset + 7);

    const id = ascii(source, offset, 4);
    const length = u32be(source, offset + 4);

    if (id === 'COMM' && length >= 18) {
      const body = offset + 8;

      await source.load(body, body + (compressed && length >= 22 ? 21 : 17));

      const channels = u16be(source, body);
      const sampleSize = u16be(source, body + 6);
      const sampleRate = extended80(source, body + 8);

      const pcm = !compressed || (length >= 22 && PCM_AIFC.has(ascii(source, body + 18, 4)));
      const bitrate = pcm ? Math.round((channels * sampleSize * sampleRate) / 1000) : undefined;

      return bitrate ? { container: 'aiff', bitrate } : { container: 'aiff' };
    }

    offset += 8 + length + (length & 1);
  }

  return { container: 'aiff' };
}

async function readOgg(source: ByteSource, start: number): Promise<AudioProperties | undefined> {
  // The first page holds the identification header alone. Its packet starts
  // after the 27 byte page header and the segment table.
  await source.load(start, start + 26);

  const segments = source.byteAt(start + 26);
  const packet = start + 27 + segments;

  await source.load(packet, packet + 29);

  if (packet + 30 <= source.size && ascii(source, packet, 7) === 'vorbis') {
    const nominal = u32le(source, packet + 20) | 0;
    const bitrate = nominal > 0 ? Math.round(nominal / 1000) : undefined;

    return bitrate ? { container: 'ogg', bitrate } : { container: 'ogg' };
  }

  // Opus and anything else in an Ogg: no bit rate in the header.
  return { container: 'ogg' };
}

/**
 * The container and bit rate of the file in `source`, or nothing when the
 * bytes are not a format this reads.
 */
export default async function readAudioProperties(
  source: ByteSource,
): Promise<AudioProperties | undefined> {
  const start = await skipId3v2(source);

  if (start + 12 > source.size) return undefined;

  await source.load(start, start + 11);

  const magic = ascii(source, start, 4);

  if (magic === 'fLaC') return readFlac(source, start);
  if (magic === 'RIFF' && ascii(source, start + 8, 4) === 'WAVE') return readWav(source, start);
  if (magic === 'FORM') {
    const form = ascii(source, start + 8, 4);

    if (form === 'AIFF' || form === 'AIFC') return readAiff(source, start);
  }
  if (magic === 'OggS') return readOgg(source, start);
  if (ascii(source, start + 4, 4) === 'ftyp') return readMp4(source, start);

  return readMp3(source, start);
}
